<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('textSort.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('textSort.title') }}</h1>
      <p>{{ $t('textSort.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="actions">
        <button class="btn primary" @click="sortAsc">{{ $t('textSort.sortAsc') }}</button>
        <button class="btn primary" @click="sortDesc">{{ $t('textSort.sortDesc') }}</button>
        <button class="btn" @click="reverseLines">{{ $t('textSort.reverse') }}</button>
        <button class="btn" @click="shuffleLines">{{ $t('textSort.shuffle') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div class="options">
        <label class="option-label">
          <input type="checkbox" v-model="ignoreCase" />
          {{ $t('textSort.ignoreCase') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="numericSort" />
          {{ $t('textSort.numericSort') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="removeEmpty" />
          {{ $t('textSort.removeEmpty') }}
        </label>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('textSort.inputLabel') }}</label>
          <textarea v-model="input" :placeholder="$t('textSort.inputPlaceholder')" spellcheck="false"></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('textSort.outputLabel') }}</label>
          <textarea v-model="output" readonly :placeholder="$t('textSort.outputPlaceholder')" spellcheck="false"></textarea>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('textSort.whatIs') }}</h2>
      <p>{{ $t('textSort.whatIsDesc') }}</p>
      <h2>{{ $t('textSort.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textSort.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('textSort.featuresTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textSort.features')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextSort',
  data() {
    return { input: '', output: '', successMessage: '', ignoreCase: false, numericSort: false, removeEmpty: true }
  },
  methods: {
    getLines() {
      let lines = this.input.split('\n')
      if (this.removeEmpty) lines = lines.filter(l => l.trim() !== '')
      return lines
    },
    sortAsc() {
      const lines = this.getLines()
      lines.sort((a, b) => {
        if (this.numericSort) return parseFloat(a) - parseFloat(b)
        const aa = this.ignoreCase ? a.toLowerCase() : a
        const bb = this.ignoreCase ? b.toLowerCase() : b
        return aa.localeCompare(bb, 'zh-CN')
      })
      this.output = lines.join('\n')
      this.showSuccess()
    },
    sortDesc() {
      const lines = this.getLines()
      lines.sort((a, b) => {
        if (this.numericSort) return parseFloat(b) - parseFloat(a)
        const aa = this.ignoreCase ? a.toLowerCase() : a
        const bb = this.ignoreCase ? b.toLowerCase() : b
        return bb.localeCompare(aa, 'zh-CN')
      })
      this.output = lines.join('\n')
      this.showSuccess()
    },
    reverseLines() {
      const lines = this.getLines()
      this.output = lines.reverse().join('\n')
      this.showSuccess()
    },
    shuffleLines() {
      const lines = this.getLines()
      for (let i = lines.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [lines[i], lines[j]] = [lines[j], lines[i]]
      }
      this.output = lines.join('\n')
      this.showSuccess()
    },
    showSuccess() {
      this.successMessage = this.$t('textSort.success')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    async copyResult() {
      if (!this.output) return
      try { await navigator.clipboard.writeText(this.output); this.successMessage = this.$t('common.copied') }
      catch { this.successMessage = this.$t('common.copyFailed') }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() { this.input = ''; this.output = ''; this.successMessage = '' }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; }
.editor-panel textarea { min-height: 350px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
